import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { CONTACT } from "@/data/site";
import { Logo } from "@/components/Logo";

const NAV = [
  {
    label: "Versicherungen",
    to: "/versicherungen",
    children: [
      { label: "Übersicht", to: "/versicherungen" },
      { label: "Versicherungsvergleich", to: "/versicherungsvergleich" },
    ],
  },
  { label: "Finanzierung", to: "/finanzierung" },
  { label: "Immobilien", to: "/immobilien" },
  { label: "Ratgeber", to: "/ratgeber" },
  { label: "Über uns", to: "/ueber-uns" },
  { label: "Kontakt", to: "/kontakt" },
];

const isActive = (item, pathname) =>
  pathname === item.to || (item.children || []).some((c) => c.to === pathname);

export function PublicHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="public-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-navy-900/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
        <Logo size={40} />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.to} className="relative group" data-testid={`nav-group-${item.to.replace("/", "")}`}>
                <Link
                  to={item.to}
                  data-testid={`nav-${item.to.replace("/", "")}`}
                  className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors duration-200 ${
                    isActive(item, location.pathname) ? "text-gold-500" : "text-slate-200 hover:text-gold-500"
                  }`}
                >
                  {item.label}
                  <ChevronDown size={14} className="transition-transform duration-200 group-hover:rotate-180" />
                </Link>
                <div className="absolute left-0 top-full pt-3 hidden group-hover:block">
                  <div className="bg-navy-800 border border-white/10 rounded-md py-2 min-w-[240px] shadow-2xl shadow-black/40">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-sub-${c.to.replace("/", "")}`}
                        className={`block px-5 py-2.5 text-sm transition-colors ${
                          location.pathname === c.to ? "text-gold-500" : "text-slate-200 hover:text-gold-500 hover:bg-white/5"
                        }`}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace("/", "")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 ${
                  location.pathname === item.to ? "text-gold-500" : "text-slate-200 hover:text-gold-500"
                }`}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a href={`tel:${CONTACT.phoneHref}`} className="flex items-center gap-2 text-slate-300 hover:text-white text-sm" data-testid="header-phone">
            <Phone size={16} className="text-gold-500" /> {CONTACT.phone}
          </a>
          <button
            data-testid="header-termin-btn"
            onClick={() => navigate("/termin-buchen")}
            className="bg-gold-500 text-navy-900 font-bold tracking-wide rounded-sm px-6 py-3 text-sm hover:bg-gold-300 transition-colors duration-200"
          >
            Termin buchen
          </button>
          <Link to="/login" data-testid="header-login" className="text-slate-300 hover:text-white text-sm">Login</Link>
        </div>

        <button className="lg:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Menü">
          {mobileOpen ? <X /> : <Menu />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-navy-900 border-t border-white/10 px-6 py-6 space-y-4" data-testid="mobile-menu">
          {NAV.map((item) => (
            <div key={item.to}>
              <Link to={item.to} className="block text-slate-200 hover:text-gold-500 text-base">{item.label}</Link>
              {item.children && (
                <div className="mt-2 ml-4 space-y-2 border-l border-white/10 pl-4">
                  {item.children.map((c) => (
                    <Link key={c.to} to={c.to} data-testid={`mnav-sub-${c.to.replace("/", "")}`} className="block text-slate-400 hover:text-gold-500 text-sm">{c.label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <button onClick={() => navigate("/termin-buchen")} className="w-full bg-gold-500 text-navy-900 font-bold rounded-sm py-3">Termin buchen</button>
          <Link to="/login" className="block text-center text-slate-300">Login</Link>
        </div>
      )}
    </header>
  );
}
